import { useQueryParams } from "@rentiohq/shared-frontend/dist/hooks/useQueryParams";
import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import * as contactSelectors from "@rentiohq/shared-frontend/dist/redux/contact/contact.selectors";
import { EContactCustomId } from "@rentiohq/shared-frontend/dist/redux/contact/contact.types";
import * as registrationActions from "@rentiohq/shared-frontend/dist/redux/registration/registration.actions";
import * as registrationSelectors from "@rentiohq/shared-frontend/dist/redux/registration/registration.selectors";
import * as contractHooks from "@rentiohq/shared-frontend/dist/reduxV2/contract/contract.hooks";
import * as propertyHooks from "@rentiohq/shared-frontend/dist/reduxV2/property/property.hooks";
import { ERegistrationDocumentType } from "@rentiohq/shared-frontend/dist/types/registration.types";
import { formatAddress } from "@rentiohq/shared-frontend/dist/utils/address.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { stringToSnakeCase } from "@rentiohq/shared-frontend/dist/utils/string.utils";
import {
  Error,
  Card,
  Loading,
  Page,
} from "@rentiohq/web-shared/dist/components";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { IRootStore } from "redux/reducers";
import { NumberParam, createEnumParam } from "serialize-query-params";
import { AddendumRegistrationForm } from "./AddendumRegistrationForm";
import { ContractRegistrationForm } from "./ContractRegistrationForm";

const RegistrationForm = () => {
  const dispatch = useDispatch();

  const { isBroker } = authHooks.useSelf();

  const { contractId, ...params } = useParams<{
    propertyId: string;
    contractId: string;
  }>();

  const propertyId = params.propertyId ? +params.propertyId : undefined;

  const [queryParams] = useQueryParams({
    id: NumberParam,
    documentType: createEnumParam(Object.values(ERegistrationDocumentType)),
    existingDocument: NumberParam,
  });

  const {
    id: registrationId,
    documentType = ERegistrationDocumentType.Contract,
  } = queryParams;

  const legalContact = useSelector((state: IRootStore) =>
    isBroker
      ? contactSelectors.getContactByCustomId(state, EContactCustomId.Legal)
      : undefined,
  );

  const isFetchingLegalContact = useSelector((state: IRootStore) =>
    isBroker
      ? state.contact.contactsById[EContactCustomId.Legal].isFetching
      : false,
  );

  const { detail: property, fetchError: propertyFetchError } =
    propertyHooks.useDetail({
      shouldRefetch: false,
      id: propertyId,
    });

  const { detail: contract, fetchError: contractFetchError } =
    contractHooks.useDetail({
      shouldRefetch: false,
      id: contractId,
    });

  const fetchError = useSelector(
    (state: IRootStore) =>
      contractFetchError ||
      propertyFetchError ||
      registrationSelectors.fetchError(state, registrationId),
  );

  const fetchedRegistration = useSelector((state: IRootStore) =>
    registrationSelectors.registration(state, registrationId),
  );

  const fetchRegistrationError = useSelector((state: IRootStore) =>
    registrationSelectors.fetchError(state, registrationId),
  );

  const isFetchingRegistration = useSelector((state: IRootStore) =>
    registrationSelectors.isFetching(state, registrationId),
  );

  React.useEffect(() => {
    if (registrationId) {
      dispatch(
        registrationActions.getRegistration.actions.start({
          id: registrationId,
        }),
      );
    }
  }, [registrationId]);

  const showAddendumRegistrationForm =
    registrationId &&
    [
      ERegistrationDocumentType.Addendum,
      ERegistrationDocumentType.PropertyInspectionReport,
      ERegistrationDocumentType.Brussels,
    ].includes(documentType);

  const title = showAddendumRegistrationForm
    ? getLocalizedText(`registration.${stringToSnakeCase(documentType)}.title`)
    : getLocalizedText("registration.create.title");

  const breadcrumbs = showAddendumRegistrationForm
    ? {
        goBack: true,
        content: getLocalizedText("system.back"),
      }
    : {
        to: `/properties/${propertyId}`,
        content: getLocalizedText("system.back"),
      };

  if (isFetchingLegalContact && !legalContact) {
    return <Loading />;
  }

  const error = fetchError || fetchRegistrationError;
  if (error) {
    return <Error errors={[error]} />;
  }

  if (!property || !contract) {
    return null;
  }

  const renderAddendumRegistrationForm = () => (
    <AddendumRegistrationForm
      property={property}
      contract={contract}
      legalContact={legalContact}
    />
  );

  const renderContractRegistrationForm = () => {
    if (isFetchingRegistration) {
      return <Loading />;
    }

    return (
      <ContractRegistrationForm
        property={property}
        contract={contract}
        legalContact={legalContact}
        registration={fetchedRegistration}
      />
    );
  };

  return (
    <Page
      title={title}
      subtitle={formatAddress(property)}
      breadcrumbs={breadcrumbs}
    >
      <Card>
        {showAddendumRegistrationForm
          ? renderAddendumRegistrationForm()
          : renderContractRegistrationForm()}
      </Card>
    </Page>
  );
};

// eslint-disable-next-line import/no-default-export
export default RegistrationForm;
